import { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import { useAiStore } from '@/store/aiStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { useVideoSync } from '@/hooks/useVideoSync.ts';
import { capabilities } from '@/capabilities.ts';
import { computeContentRect, renderShotMedia, FullscreenBtn, parseAspectRatio, deriveViewerFrame, pickDisplayThumbnail } from './ViewerContent.tsx';
import { ViewerOverlay } from './ViewerOverlay.tsx';
import { getReviewerColor } from '@/utils/reviewerColor.ts';
import { getCachedAssetBytes, readAssetBytes, toArrayBuffer } from '@/previs/assetStorage.ts';
import { diagLog } from '@/bridge/embeddedBridge.ts';
import { useShotImageDrop } from '@/hooks/useShotImageDrop.ts';
import { useShotFullImage } from '@/hooks/useShotFullImage.ts';
import { ShotImageDropConfirm } from './ShotImageDropConfirm.tsx';
import { shotHasVideo } from '@/store/projectDerived.ts';
import styles from './Viewer.module.css';

// ── Trackpad wheel swipe (two-finger navigate) ────────────────────────
// Accumulated horizontal wheel distance that counts as a swipe — mirrors the 60px
// one-finger pointer-swipe threshold in handlePointerUp.
const WHEEL_SWIPE_THRESHOLD_PX = 60;
// Quiet time between wheel events that ends one gesture: a trackpad swipe (momentum
// tail included) streams events every ~16ms, so 150ms of silence means the fingers
// lifted and the next burst is a new gesture.
const WHEEL_SWIPE_GESTURE_GAP_MS = 150;

function scrollLaneToTime(time: number) {
  const lane = document.querySelector('[data-orient="shot-lane"]')?.querySelector('[class*="lane"]') as HTMLElement | null;
  if (!lane) return;
  const pps = useUiStore.getState().pixelsPerSecond;
  const x = time * pps;
  const { clientWidth } = lane;
  lane.scrollLeft = Math.max(0, x - clientWidth / 2);
}


export function Viewer() {
  const { t } = useTranslation('viewer');

  // ── Store selectors ────────────────────────────────────────────────
  const currentTime = usePlaybackStore(s => s.currentTime);
  const getShotAtTime = useProjectStore(s => s.getShotAtTime);
  const getSceneForShot = useProjectStore(s => s.getSceneForShot);
  const getTransitionAtTime = useProjectStore(s => s.getTransitionAtTime);
  const getContinuousChain = useProjectStore(s => s.getContinuousChain);
  const getActiveContinuousPair = useProjectStore(s => s.getActiveContinuousPair);
  const getShotStartTime = useProjectStore(s => s.getShotStartTime);
  // isFullscreen used by TimelineView.tsx, not here
  const project = useProjectStore(s => s.project);
  const drawingMode = useUiStore(s => s.drawingMode);
  const setDrawingMode = useUiStore(s => s.setDrawingMode);
  const reviewerName = useCommentStore(s => s.reviewerName);
  const showInputImage = useAiStore(s => s.showInputImage);
  const getInputImageUrl = useAiStore(s => s.getInputImageUrl);
  const allComments = useCommentStore(s => s.comments);

  // ── Derived data ───────────────────────────────────────────────────
  const shot = getShotAtTime(currentTime);
  const scene = shot ? getSceneForShot(shot.id) : null;
  // 20260929 - the frame derivation (transition filter, dissolve, fade, primary shot, sync shot) is
  // shared with the laptop pose's passive mirror (TimelinePlaybackMirror) so the two cannot drift.
  const frame = deriveViewerFrame(shot, getTransitionAtTime(currentTime));
  const { transInfo, dissolve } = frame;
  const chain = shot ? getContinuousChain(shot.id) : null;
  const activePair = shot ? getActiveContinuousPair(shot.id) : null;
  const activePairIndex = chain ? (chain.shotIndex === 0 ? 0 : chain.shotIndex - 1) : -1;
  const nextPair = chain && activePairIndex >= 0 && activePairIndex + 1 < chain.pairs.length
    ? chain.pairs[activePairIndex + 1] : null;

  // ── Full-res static image (embedded) ───────────────────────────────
  // In embedded mode a static shot arrives carrying only Unity's reduced cache thumbnail (the slim
  // DB ships no blob), which is right for tiles and soft at viewer size. The real pixels are asked
  // for on demand and swapped in below when they land. Both this and isDissolve live up here rather
  // than beside the display expression because a hook cannot be called past the early returns —
  // isDissolve decides WHICH shot the big <img> shows, so the request has to follow it.
  // (20260929: both now come from deriveViewerFrame above — `dissolve` and `frame.fullResShot`.)
  const fullResUrl = useShotFullImage(frame.fullResShot);

  // ── Image drop → confirm → static image (QA ask: smoother entry than the Import tab) ──
  // One handlers object + one confirm element, used on EVERY return branch below (five
  // roots) — wiring only the thumbnail branch would make the drop work on shots that
  // already have an image and silently fail on the empty/placeholder shots users most
  // want to fill. Inert when readOnly or no shot resolves at the playhead.
  const readOnly = useProjectStore(s => s.readOnly);
  const imageDrop = useShotImageDrop(shot?.id ?? null, readOnly || !shot);
  const viewerClass = imageDrop.dropActive ? `${styles.viewer} ${styles.viewerDropActive}` : styles.viewer;
  // Label/media facts come from the shot the file was DROPPED on (pendingShotId), not the
  // shot at the playhead — playback can move between the drop and the Confirm tap.
  const pendingShot = imageDrop.pendingShotId == null
    ? null
    : project?.scenes.flatMap(sc => sc.shots).find(sh => sh.id === imageDrop.pendingShotId) ?? null;
  // Fail closed: if the dropped-on shot disappears (deleted, project reload) while the
  // dialog is up, clear the pending state — otherwise the dialog silently vanishes with
  // the File stranded and can pop back up later, unrelated to any user action.
  const pendingVanished = imageDrop.pendingShotId != null && !pendingShot;
  const cancelDrop = imageDrop.cancel;
  useEffect(() => {
    if (pendingVanished) cancelDrop();
  }, [pendingVanished, cancelDrop]);
  const dropConfirmEl = imageDrop.pendingFile && pendingShot ? (
    <ShotImageDropConfirm
      shotLabel={pendingShot.number}
      fileName={imageDrop.pendingFile.name}
      hasExistingMedia={pendingShot.hasStaticImage === true || shotHasVideo(pendingShot) || pendingShot.isAnimatic === true}
      onConfirm={imageDrop.confirm}
      onCancel={imageDrop.cancel}
    />
  ) : null;

  // ── Refs ───────────────────────────────────────────────────────────
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const transCanvasRef = useRef<HTMLCanvasElement>(null);
  // continuousCanvasRef removed — continuous transition video rendering deprecated
  const viewerRef = useRef<HTMLDivElement>(null);

  // ── Content rect tracking ──────────────────────────────────────────
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = viewerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerSize({ w: entry.contentRect.width, h: entry.contentRect.height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onDimensions = useCallback((w: number, h: number) => {
    if (w > 0 && h > 0) setNaturalSize({ w, h });
  }, []);

  const imgRefCallback = useCallback((img: HTMLImageElement | null) => {
    if (!img) return;
    const update = () => { if (img.naturalWidth > 0) setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight }); };
    if (img.complete && img.naturalWidth > 0) { update(); } else { img.addEventListener('load', update, { once: true }); }
  }, []);

  useEffect(() => {
    if (!shot?.videoObjectUrl && !shot?.thumbnailDataUrl) setNaturalSize({ w: 0, h: 0 });
  }, [shot?.videoObjectUrl, shot?.thumbnailDataUrl]);

  // Use project aspect ratio for letterboxing (matches Unity canvas editor viewport):
  const projectAR = useMemo(() => {
    if (!project?.aspectRatio) return 0;
    return parseAspectRatio(project.aspectRatio);
  }, [project?.aspectRatio]);

  const contentRect = useMemo(() => {
    if (projectAR > 0) {
      return computeContentRect(containerSize.w, containerSize.h, 1000, 1000 / projectAR);
    }
    return computeContentRect(containerSize.w, containerSize.h, naturalSize.w, naturalSize.h);
  }, [containerSize.w, containerSize.h, projectAR, naturalSize.w, naturalSize.h]);
  const hasContentRect = contentRect.width > 0 && contentRect.height > 0;

  // ── Reviewer color ─────────────────────────────────────────────────
  const reviewerColor = useMemo(
    () => getReviewerColor(reviewerName),
    [reviewerName],
  );

  // ── Annotation management ──────────────────────────────────────────
  const pendingPathsRef = useRef<{ d: string; stroke: string; strokeWidth: number }[]>([]);
  const handlePathsChange = useCallback((paths: { d: string; stroke: string; strokeWidth: number }[]) => {
    pendingPathsRef.current = paths;
  }, []);

  const prevDrawingMode = useRef(drawingMode);
  useEffect(() => {
    if (prevDrawingMode.current && !drawingMode) {
      const paths = pendingPathsRef.current;
      if (paths.length > 0) {
        const shotId = useProjectStore.getState().getShotAtTime(usePlaybackStore.getState().currentTime)?.id;
        if (shotId != null) {
          useUiStore.getState().openPostDrawPrompt(shotId, [...paths]);
        }
        pendingPathsRef.current = [];
      }
    }
    prevDrawingMode.current = drawingMode;
  }, [drawingMode]);

  const prevShotId = useRef(shot?.id);
  useEffect(() => {
    if (shot?.id !== prevShotId.current) {
      prevShotId.current = shot?.id;
      if (drawingMode) setDrawingMode(false);
    }
  }, [shot?.id, drawingMode, setDrawingMode]);

  useEffect(() => {
    if (!drawingMode) return;
    const handler = (e: PointerEvent) => {
      const el = viewerRef.current;
      if (!el) return;
      if (el.contains(e.target as Node)) return;
      setDrawingMode(false);
    };
    window.addEventListener('pointerdown', handler, true);
    return () => window.removeEventListener('pointerdown', handler, true);
  }, [drawingMode, setDrawingMode]);

  // ── Video sync ─────────────────────────────────────────────────────
  // During dissolve/continuous transitions, videoRef is always attached to
  // fromShot and transVideoRef to toShot — lock sync targets accordingly
  // so they don't swap at the progress midpoint.
  const videoSyncShot = frame.videoSyncShot;
  const videoSyncStart = videoSyncShot ? getShotStartTime(videoSyncShot.id) : 0;
  const [primaryVideoData, setPrimaryVideoData] = useState<ArrayBuffer | null>(null);
  const primaryVideoUrl = videoSyncShot?.videoObjectUrl;
  useEffect(() => {
    if (!primaryVideoUrl) { setPrimaryVideoData(null); return; }
    const cached = getCachedAssetBytes(primaryVideoUrl);
    if (cached) {
      diagLog('Viewer', `primary video: cache HIT for ${primaryVideoUrl.slice(0, 40)}… (${cached.byteLength} bytes)`);
      setPrimaryVideoData(toArrayBuffer(cached));
    } else {
      diagLog('Viewer', `primary video: cache MISS for ${primaryVideoUrl.slice(0, 40)}… — fetching`);
      readAssetBytes(primaryVideoUrl).then(bytes => {
        if (bytes) {
          diagLog('Viewer', `primary video: fetch OK (${bytes.byteLength} bytes)`);
          setPrimaryVideoData(toArrayBuffer(bytes));
        } else {
          diagLog('Viewer', 'primary video: fetch FAILED — no bytes available');
        }
      });
    }
  }, [primaryVideoUrl]);
  // 20260929 - 'primary' / 'transition': these two PAINTED decoders also feed the laptop pose's playback
  // mirror (video/videoMirror.ts) — one decode, two pictures. The next-pair pre-seek below does not.
  useVideoSync(canvasRef, primaryVideoData, videoSyncStart, videoSyncShot?.duration ?? 0, onDimensions, 'primary');

  const transPartner = transInfo ? transInfo.toShot : null;
  const transPartnerStart = transPartner ? getShotStartTime(transPartner.id) : 0;
  const transVideoData = useMemo(() => {
    const bytes = transPartner?.videoObjectUrl ? getCachedAssetBytes(transPartner.videoObjectUrl) : undefined;
    return bytes ? toArrayBuffer(bytes) : null;
  }, [transPartner?.videoObjectUrl]);
  useVideoSync(transCanvasRef, transVideoData, transPartnerStart, transPartner?.duration ?? 0, undefined, 'transition');

  // Continuous transition video sync removed — continuous type is deprecated.
  // nextPair video preload (below) is also removed since it was only used for continuous transitions.

  const nextPairHasVideo = !!nextPair?.transition.videoObjectUrl;
  const nextFromStart = nextPairHasVideo ? getShotStartTime(nextPair!.fromShot.id) : 0;
  const nextSpanDuration = nextPairHasVideo ? nextPair!.fromShot.duration + nextPair!.toShot.duration : 0;
  const nextPairVideoData = useMemo(() => {
    const url = nextPair?.transition.videoObjectUrl;
    const bytes = url ? getCachedAssetBytes(url) : undefined;
    return bytes ? toArrayBuffer(bytes) : null;
  }, [nextPair?.transition.videoObjectUrl]);
  useVideoSync(null, nextPairVideoData, nextFromStart, nextSpanDuration);

  // ── Fullscreen ─────────────────────────────────────────────────────
  const toggleFullscreen = useUiStore(s => s.toggleFullscreen);
  const handleFullscreen = useCallback(() => {
    toggleFullscreen();
  }, [toggleFullscreen]);

  // ── Swipe to skip shot ────────────────────────────────────────────
  const isRecording = useUiStore(s => s.isRecording);
  const isAnyGenerating = useAiStore(s => s.isAnyGenerating);
  const seek = usePlaybackStore(s => s.seek);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const canSwipe = !drawingMode && !isRecording && !isAnyGenerating();

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!canSwipe) return;
    swipeStartRef.current = { x: e.clientX, y: e.clientY };
  }, [canSwipe]);

  // Navigation core shared by the one-finger pointer swipe and the trackpad wheel
  // swipe: negative dx → next shot boundary, positive dx → previous shot start.
  const navigateFromSwipeDx = useCallback((dx: number) => {
    const selectShot = useProjectStore.getState().selectShot;
    const allShots = project?.scenes.flatMap(s => s.shots) ?? [];
    if (dx < 0) {
      // Swipe left → next shot
      let cumulative = 0;
      for (let i = 0; i < allShots.length; i++) {
        cumulative += allShots[i].duration;
        if (cumulative > currentTime + 0.05) {
          const nextShot = allShots[i + 1] ?? allShots[i];
          selectShot(nextShot.id);
          seek(cumulative);
          scrollLaneToTime(cumulative);
          return;
        }
      }
    } else {
      // Swipe right → previous shot
      let cumulative = 0;
      let prevStart = 0;
      let prevShot = allShots[0];
      for (let i = 0; i < allShots.length; i++) {
        if (cumulative >= currentTime - 0.05) break;
        prevStart = cumulative;
        prevShot = allShots[i];
        cumulative += allShots[i].duration;
      }
      selectShot(prevShot.id);
      seek(prevStart);
      scrollLaneToTime(prevStart);
    }
  }, [project, currentTime, seek]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!swipeStartRef.current || !canSwipe) return;
    const dx = e.clientX - swipeStartRef.current.x;
    const dy = e.clientY - swipeStartRef.current.y;
    swipeStartRef.current = null;
    // Only horizontal swipes (not vertical scrolls)
    if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx)) return;
    navigateFromSwipeDx(dx);
  }, [canSwipe, navigateFromSwipeDx]);

  // Two-finger trackpad swipes never reach the pointer handlers: macOS WKWebView
  // (standalone + Editor, and iPadOS with a trackpad) delivers them as DOM wheel
  // events with pixel deltas and a momentum tail. Accumulate per gesture in refs —
  // no re-renders — and fire the shared navigation core once per gesture.
  const wheelAccumRef = useRef({ x: 0, y: 0 });
  const wheelLastTsRef = useRef(0);
  const wheelFiredRef = useRef(false);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    const now = performance.now();
    // A quiet gap ends the previous gesture (momentum tail included) — no timer
    // needed, the check runs on the next event.
    if (now - wheelLastTsRef.current > WHEEL_SWIPE_GESTURE_GAP_MS) {
      wheelAccumRef.current = { x: 0, y: 0 };
      wheelFiredRef.current = false;
    }
    wheelLastTsRef.current = now;
    wheelAccumRef.current.x += e.deltaX;
    wheelAccumRef.current.y += e.deltaY;
    // Momentum tail holds the latch (lastTs keeps updating above); a quiet gap re-arms.
    if (wheelFiredRef.current) return;
    const { x, y } = wheelAccumRef.current;
    // Horizontal dominance excludes mouse/vertical scroll wheels.
    if (Math.abs(x) < WHEEL_SWIPE_THRESHOLD_PX || Math.abs(x) <= Math.abs(y)) return;
    // Latch before the guard: crossing the threshold consumes the gesture either way,
    // so canSwipe flipping true mid-momentum can't fire a stale swipe.
    wheelFiredRef.current = true;
    if (!canSwipe) return;
    // Sign mapping: with natural scrolling, fingers moving LEFT produce POSITIVE
    // deltaX, while a one-finger touch swipe LEFT produces NEGATIVE pointer dx and
    // navigates to the NEXT shot — negate so two-finger-left also advances, matching touch.
    navigateFromSwipeDx(-x);
  }, [canSwipe, navigateFromSwipeDx]);

  // transVideoCountdown removed — was only used for continuous transition video actions

  // ── Derived display data ───────────────────────────────────────────
  const unresolvedDoodleSvgs = useMemo(() => {
    if (!shot) return [];
    return allComments
      .filter(c => c.shotId === shot.id && c.annotationSvg)
      .map(c => c.annotationSvg!);
  }, [allComments, shot]);

  const isInTransition = !!transInfo;
  const canDraw = !isInTransition && !!shot && capabilities.comments;

  // ── Apple Pencil auto-annotate ──────────────────────────────────────
  const [initialPenPoint, setInitialPenPoint] = useState<{ clientX: number; clientY: number } | null>(null);

  const handlePenDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType !== 'pen') return;
    if (drawingMode) return;
    if (!canDraw) return;
    setInitialPenPoint({ clientX: e.clientX, clientY: e.clientY });
    useUiStore.getState().setDrawingMode(true);
  }, [drawingMode, canDraw]);

  // Clear initial pen point when drawing mode ends
  useEffect(() => {
    if (!drawingMode) setInitialPenPoint(null);
  }, [drawingMode]);

  // ── Pencil squeeze bridge (called from Unity native plugin) ─────────
  useEffect(() => {
    window.__pencilAction = () => {
      const ui = useUiStore.getState();

      // If script panel is open, toggle screenplay annotation mode
      if (ui.scriptPanelOpen) {
        ui.toggleScriptAnnotationMode();
        return;
      }

      // Otherwise toggle viewer drawing mode (existing behavior)
      if (ui.drawingMode) {
        ui.setDrawingMode(false);
      } else {
        if (!useProjectStore.getState().getShotAtTime(usePlaybackStore.getState().currentTime)) return;
        ui.setDrawingMode(true);
      }
    };
    return () => { delete window.__pencilAction; };
  }, []);

  const overlayProps = shot ? {
    shot, contentRect, hasContentRect, activePair, transInfo,
    unresolvedDoodleSvgs, canDraw, reviewerColor, onFullscreen: handleFullscreen, onPathsChange: handlePathsChange,
    initialPenPoint,
  } : null;

  // ── Empty state ────────────────────────────────────────────────────
  // No swipe/wheel wiring here: getShotAtTime clamps any time to the last shot, so this
  // branch only renders when the project has no shots at all — nothing to navigate to.
  if (!shot || !scene) {
    return (
      <div ref={viewerRef} className={viewerClass} {...imageDrop.handlers}>
        <div className={styles.empty}>{t('noShotsLoaded')}</div>
        <FullscreenBtn onClick={handleFullscreen} disabled={!project} />
      </div>
    );
  }

  // ── Aspect-ratio crop style (shared by images AND videos) ─────────
  // When projectAR is set, position media within the contentRect and use
  // object-fit: cover to center-crop to the project aspect ratio. This
  // matches Unity's viewport cropping for both static images and videos.
  const cropStyle: React.CSSProperties | undefined = hasContentRect && projectAR > 0 ? {
    position: 'absolute',
    left: contentRect.x,
    top: contentRect.y,
    width: contentRect.width,
    height: contentRect.height,
    objectFit: 'cover',
  } : undefined;

  // ── Transition rendering ───────────────────────────────────────────
  // 'continuous' transition type is deprecated — video path removed.
  // Legacy projects with continuous transitions fall through to the dissolve path below.
  // (20260929: the fade's half and opacity come from deriveViewerFrame — frame.fade.)
  if (transInfo) {
    if (frame.fade) {
      const { shot: activeShot, opacity: fadeOpacity } = frame.fade;
      const activeScene = getSceneForShot(activeShot.id);
      return (
        <div ref={viewerRef} className={viewerClass} style={{ background: '#000' }} onPointerDown={e => { handlePenDown(e); handlePointerDown(e); }} onPointerUp={handlePointerUp} onWheel={handleWheel} {...imageDrop.handlers}>
          {renderShotMedia(activeShot, activeScene?.color ?? '#333', fadeOpacity, canvasRef, showInputImage ? getInputImageUrl(activeShot.historyId) : null)}
          <FullscreenBtn onClick={handleFullscreen} />
          {dropConfirmEl}
        </div>
      );
    }
  }

  // ── Dissolve / standard unified rendering ──────────────────────────
  // Dissolve and standard shots share the same render tree so the primary
  // <video> element stays mounted across transition boundaries (no flash).
  // (`dissolve` is derived with the full-res request near the top — hooks must run above the
  // early returns this section sits below.)
  const primaryShot = frame.primaryShot ?? shot;
  const primaryOpacity = frame.primaryOpacity;
  const inputUrl = showInputImage ? getInputImageUrl(primaryShot.historyId) : null;
  // fullResUrl is null until Unity answers (and stays null when it has nothing), so the cache
  // thumbnail paints first and sharpens in place. It sits UNDER inputUrl: the AI input-image
  // toggle is an explicit user override of what the viewer shows.
  const displayThumbnail = pickDisplayThumbnail(inputUrl, fullResUrl, primaryShot.thumbnailDataUrl);
  const primaryOpacityStyle = primaryOpacity < 1 ? { opacity: primaryOpacity } : undefined;

  if (primaryShot.videoObjectUrl) {
    // Canvas style: strip objectFit (doesn't work on canvas), merge dissolve opacity.
    // cropStyle's explicit width/height from contentRect forces the canvas to fill the
    // crop rectangle; drawImage scales the video to fill the canvas internally.
    const canvasStyle = cropStyle
      ? { ...cropStyle, objectFit: undefined, transform: 'none', ...primaryOpacityStyle }
      : primaryOpacityStyle;
    return (
      <div ref={viewerRef} className={viewerClass} onPointerDown={e => { handlePenDown(e); handlePointerDown(e); }} onPointerUp={handlePointerUp} onWheel={handleWheel} {...imageDrop.handlers}>
        <canvas ref={canvasRef} className={styles.videoCanvas} style={canvasStyle} />
        {dissolve && renderShotMedia(dissolve.toShot, getSceneForShot(dissolve.toShot.id)?.color ?? '#333', dissolve.progress, transCanvasRef, showInputImage ? getInputImageUrl(dissolve.toShot.historyId) : null)}
        {dissolve ? <FullscreenBtn onClick={handleFullscreen} /> : <ViewerOverlay {...overlayProps!} />}
        {dropConfirmEl}
      </div>
    );
  }

  if (displayThumbnail) {
    return (
      <div ref={viewerRef} className={viewerClass} onPointerDown={e => { handlePenDown(e); handlePointerDown(e); }} onPointerUp={handlePointerUp} onWheel={handleWheel} {...imageDrop.handlers}>
        <img ref={imgRefCallback} className={styles.thumbnail} src={displayThumbnail} alt={t('shotAltText', { number: primaryShot.number })} style={cropStyle ? { ...cropStyle, ...primaryOpacityStyle } : primaryOpacityStyle} />
        {dissolve && renderShotMedia(dissolve.toShot, getSceneForShot(dissolve.toShot.id)?.color ?? '#333', dissolve.progress, transCanvasRef, showInputImage ? getInputImageUrl(dissolve.toShot.historyId) : null)}
        {dissolve ? <FullscreenBtn onClick={handleFullscreen} /> : <ViewerOverlay {...overlayProps!} />}
        {dropConfirmEl}
      </div>
    );
  }

  // filter(Boolean)-joined so an empty location name (no location set) doesn't leave a stray
  // double space; mirrors composeSceneCaptionRest (sceneCaption.ts).
  const placeholderHeading = [
    [scene.locationType ? `${scene.locationType}.` : '', scene.name.toUpperCase()].filter(Boolean).join(' '),
    scene.timeOfDay,
  ].filter(Boolean).join(' \u2014 ');
  const placeholderMeta: string[] = [];
  if (shot.shotSize) placeholderMeta.push(shot.shotSize);
  if (shot.shotAngle) placeholderMeta.push(shot.shotAngle);
  if (shot.shotMovement) placeholderMeta.push(shot.shotMovement);

  return (
    <div ref={viewerRef} className={viewerClass} onPointerDown={e => { handlePenDown(e); handlePointerDown(e); }} onPointerUp={handlePointerUp} onWheel={handleWheel}
      style={{ background: `linear-gradient(135deg, ${scene.color} 0%, ${scene.color}88 50%, ${scene.color}44 100%)` }}
      {...imageDrop.handlers}>
      <div className={styles.placeholder}>
        <div className={styles.shotNumber}>{shot.number || '?'}</div>
        <div className={styles.sceneHeading}>{placeholderHeading}</div>
        <div className={styles.lensBadge}>{shot.focalLength}mm &nbsp; f/{shot.fStop}</div>
        {placeholderMeta.length > 0 && <div className={styles.metaBadge}>{placeholderMeta.join(' / ')}</div>}
        {shot.notes && <div className={styles.notes}>{shot.notes}</div>}
        {shot.name && <div className={styles.notes} style={{ opacity: 0.6 }}>{shot.name}</div>}
      </div>
      <ViewerOverlay {...overlayProps!} />
      {dropConfirmEl}
    </div>
  );
}
